// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_MESSAGES } from '@/api/constants';
import { hasProjectEndsBeforeItStartsError } from '@/api/error-utils';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { DateRangeShortcut } from '@/components/form-date-range-picker';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Project } from '@/project/api/types';
import { ProjectStatusChip } from '@/project/components/project-status-chip';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import { getInterval } from '@/project/utilities/get-interval';
import { dateSchema } from '@/utilities/date-time-schema';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Popover,
  Stack,
  Theme,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import { scaleLinear } from '@visx/scale';
import { clamp } from 'lodash-es';
import { DateTime, Interval } from 'luxon';
import { useRef } from 'react';
import {
  SubmitHandler,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';

/**
 * Props for the ProgressIndicator component
 */
interface ProgressIndicatorProps {
  /** The current progress percentage (0-100) */
  percent: number;
  /** The theme to use for colors */
  theme: Theme;
}

/**
 * A styled progress indicator that shows completion state
 */
const ProgressIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  borderRadius: 'calc(1px * infinity)',
  height: '14px',
  width: '14px',
  border: `2px solid ${theme.palette.background.paper}`,
}));

/**
 * A progress bar component that shows completion state
 */
function ProgressBar({ percent, theme }: ProgressIndicatorProps) {
  const isComplete = percent === 100;
  const indicatorColor = isComplete
    ? theme.palette.grey[400]
    : theme.palette.primary.main;
  const trackColor = isComplete
    ? theme.palette.grey[400]
    : theme.palette.grey[200];

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 'calc(1px * infinity)',
        backgroundColor: theme.palette.grey[200],
        height: '6px',
        width: '100%',
        background: `linear-gradient(to right, 
          ${indicatorColor} 0%, 
          ${indicatorColor} ${percent}%, 
          ${trackColor} ${percent}%, 
          ${trackColor} 100%
        )`,
      }}
    >
      {!isComplete && (
        <ProgressIndicator
          sx={{
            left: `calc(${percent}% - 7px)`,
            backgroundColor: indicatorColor,
          }}
        />
      )}
    </Box>
  );
}

/**
 * Calculates a human-readable duration label from an interval.
 * The calculation is INCLUSIVE of both start and end dates.
 * For example: Jan 1 to Dec 31 = 1 year (365 days inclusive).
 *
 * @param interval - The Luxon interval to calculate duration for.
 */
function getDurationLabel(interval: Interval) {
  // Add 1 day to make the interval inclusive of the end date
  // Luxon intervals are exclusive of the end, but project date ranges are inclusive
  const inclusiveEnd = interval.end.plus({ days: 1 });
  const inclusiveInterval = Interval.fromDateTimes(
    interval.start,
    inclusiveEnd
  );

  const years = Math.floor(inclusiveInterval.length('years'));

  const totalMonths = inclusiveInterval.length('months');
  const months = Math.floor(totalMonths % 12);

  const start = inclusiveInterval.start;
  const monthsEnd = start.plus({ years, months });
  const remainingDays = Math.floor(inclusiveEnd.diff(monthsEnd, 'days').days);

  const labels = [];

  if (remainingDays > 0) {
    labels.push(pluralize(remainingDays, { singular: 'day' }));
  }

  if (months > 0) {
    labels.unshift(pluralize(months, { singular: 'month' }));
  }

  if (years > 0) {
    labels.unshift(pluralize(years, { singular: 'year' }));
  }

  if (labels.length === 0) {
    labels.push(pluralize(0, { singular: 'day' }));
  }

  return labels.join(', ');
}

const formSchema = z.object({
  dateRange: z.tuple([dateSchema.nullable(), dateSchema.nullable()]),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  project: Project;

  /**
   * If true, the date range can be edited.
   *
   * @default true
   */
  editable?: boolean;

  /**
   * Display variant for the timeline.
   * - 'compact': Fixed 300px width, suitable for tables and sidebars
   * - 'wide': Full width with labeled dates, suitable for detail page headers
   *
   * @default 'compact'
   */
  variant?: 'compact' | 'wide';
}

export function ProjectDateRangeControl(props: Props) {
  const theme = useTheme();
  const editor = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { control, formState, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { dateRange: [props.project.startOn, props.project.endOn] },
  });

  const dateRange = useWatch({ control, name: 'dateRange' });
  const dateRangeController = useController({ control, name: 'dateRange' });

  const updateProjectMutation = useUpdateProject(props.project.key, {
    onSuccess: () => {
      editor.close();
    },
  });

  const updateErrorMessage =
    updateProjectMutation.isError &&
    hasProjectEndsBeforeItStartsError(updateProjectMutation.error)
      ? ERROR_MESSAGES.project_ends_before_it_starts
      : 'An error occurred while updating the project.';

  const shouldUpdate = formState.isDirty;
  const interval = getInterval(props.project);
  const durationLabel = interval === null ? null : getDurationLabel(interval);

  const scale = scaleLinear({
    domain: [interval?.start.toMillis() ?? 0, interval?.end.toMillis() ?? 0],
    range: [0, 100],
  });

  const percent = clamp(scale(DateTime.now().toMillis()), 0, 100);

  const dateRangeStart =
    dateRange === null || dateRange[0] === null
      ? DateTime.local().startOf('day')
      : dateRange[0];

  const dateRangeShortcuts: DateRangeShortcut[] = [
    {
      label: '30 days',
      getValue: () => [dateRangeStart, dateRangeStart.plus({ days: 30 })],
    },
    {
      label: '90 days',
      getValue: () => [dateRangeStart, dateRangeStart.plus({ days: 90 })],
    },
    {
      label: '6 months',
      getValue: () => [
        dateRangeStart,
        dateRangeStart.plus({ months: 6 }).minus({ days: 1 }),
      ],
    },
    {
      label: '12 months',
      getValue: () => [
        dateRangeStart,
        dateRangeStart.plus({ months: 12 }).minus({ days: 1 }),
      ],
    },
    {
      label: '24 months',
      getValue: () => [
        dateRangeStart,
        dateRangeStart.plus({ months: 24 }).minus({ days: 1 }),
      ],
    },
    {
      label: '36 months',
      getValue: () => [
        dateRangeStart,
        dateRangeStart.plus({ months: 36 }).minus({ days: 1 }),
      ],
    },
  ];

  const close = () => {
    editor.close();
    reset();
  };

  const handleClear = () => {
    dateRangeController.field.onChange([null, null]);
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (shouldUpdate) {
      if (data.dateRange === null) {
        return updateProjectMutation.mutate({ start: null, end: null });
      }

      const [start, end] = data.dateRange;

      return updateProjectMutation.mutate({
        start,
        end,
      });
    }
  };

  const isWide = props.variant === 'wide';
  const now = DateTime.now();

  /**
   * Calculates the progress text based on project status.
   * Returns an object with elapsed/remaining parts for active projects (for responsive layout),
   * or a simple string for other statuses.
   */
  const getProgressTextParts = (): {
    elapsed?: string;
    remaining?: string;
    simple?: string;
  } | null => {
    const { startOn, endOn, status } = props.project;

    if (status === 'ACTIVE' && startOn !== null && endOn !== null) {
      const daysElapsed = Math.floor(now.diff(startOn, 'days').days);
      const daysRemaining = Math.floor(endOn.diff(now, 'days').days);
      return {
        elapsed: `${pluralize(Math.max(0, daysElapsed), { singular: 'day' })} elapsed`,
        remaining: `${pluralize(Math.max(0, daysRemaining), { singular: 'day' })} remaining`,
      };
    }

    if (status === 'UPCOMING' && startOn !== null) {
      const daysUntilStart = Math.floor(startOn.diff(now, 'days').days);
      return {
        simple: `${pluralize(Math.max(0, daysUntilStart), { singular: 'day' })} until start`,
      };
    }

    if (status === 'PAST' && endOn !== null) {
      const daysSinceEnd = Math.floor(now.diff(endOn, 'days').days);
      return {
        simple: `${pluralize(Math.max(0, daysSinceEnd), { singular: 'day' })} since end`,
      };
    }

    return null;
  };

  const progressTextParts = getProgressTextParts();

  /**
   * Compact content for tables and sidebars (fixed 300px width)
   */
  const compactContent = (
    <Stack spacing={2} width={300}>
      <Stack
        spacing={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <ProjectStatusChip status={props.project.status} />
        <Typography variant="body2">{durationLabel}</Typography>
      </Stack>
      <ProgressBar percent={percent} theme={theme} />
      <Stack
        spacing={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {props.project.startOn === null ? (
          <Typography variant="body2" color="text.secondary">
            Not set
          </Typography>
        ) : (
          <Typography variant="body2">
            {props.project.startOn.toLocaleString({ dateStyle: 'medium' })}
          </Typography>
        )}
        {props.project.endOn === null ? (
          <Typography variant="body2" color="text.secondary">
            Not set
          </Typography>
        ) : (
          <Typography variant="body2">
            {props.project.endOn.toLocaleString({ dateStyle: 'medium' })}
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  /**
   * Wide content for detail page headers (full width with labeled dates)
   */
  const wideContent = (
    <Stack spacing={2} width="100%">
      {/* Top row: Status chip (left), progress text (center) */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <ProjectStatusChip status={props.project.status} />
        {progressTextParts !== null && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
            }}
          >
            {progressTextParts.simple ? (
              <Typography variant="body2" color="text.secondary">
                {progressTextParts.simple}
              </Typography>
            ) : (
              <>
                {/* Desktop: inline with bullet separator */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                >
                  {progressTextParts.elapsed} • {progressTextParts.remaining}
                </Typography>
                {/* Mobile: stacked on two lines */}
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                  <Typography variant="body2" color="text.secondary">
                    {progressTextParts.elapsed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progressTextParts.remaining}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}
        {/* Empty box to maintain space-between layout */}
        <Box width={24} />
      </Stack>

      {/* Progress bar */}
      <ProgressBar percent={percent} theme={theme} />

      {/* Bottom row: Start (left), Duration (center), End (right) */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ position: 'relative' }}
      >
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Start
          </Typography>
          {props.project.startOn === null ? (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          ) : (
            <Typography variant="body2">
              {props.project.startOn.toLocaleString({ dateStyle: 'medium' })}
            </Typography>
          )}
        </Stack>

        <Stack
          spacing={0.5}
          alignItems="center"
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Duration
          </Typography>
          <Typography
            variant="body2"
            color={durationLabel ? 'text.primary' : 'text.secondary'}
          >
            {durationLabel ?? 'Not set'}
          </Typography>
        </Stack>

        <Stack spacing={0.5} alignItems="flex-end">
          <Typography variant="caption" color="text.secondary">
            End
          </Typography>
          {props.project.endOn === null ? (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          ) : (
            <Typography variant="body2">
              {props.project.endOn.toLocaleString({ dateStyle: 'medium' })}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );

  const content = isWide ? wideContent : compactContent;

  return (
    <>
      <SupportSnackbar
        isOpen={updateProjectMutation.isError}
        onClose={updateProjectMutation.reset}
        message={updateErrorMessage}
      />
      {props.editable !== false && (
        <Popover
          anchorEl={buttonRef.current}
          open={editor.isOpen}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Stack
            component="form"
            onSubmit={handleSubmit(handleValidSubmission)}
          >
            <StaticDateRangePicker
              value={dateRangeController.field.value}
              onChange={dateRangeController.field.onChange}
              calendars={2}
              slotProps={{
                toolbar: { toolbarFormat: 'MMMM dd, yyyy' },
                shortcuts: { items: dateRangeShortcuts },
                actionBar: { actions: [] },
              }}
            />
            <Stack
              spacing={1}
              padding={1}
              direction="row"
              justifyContent="space-between"
            >
              <Button onClick={handleClear}>Clear</Button>
              <Stack spacing={1} direction="row">
                <Button onClick={close}>Cancel</Button>
                <Button type="submit" color="secondary" variant="contained">
                  Save
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Popover>
      )}
      {props.editable !== false ? (
        <EditablePropertyButton
          ref={buttonRef}
          onClick={editor.open}
          fullWidth={isWide}
          sx={
            isWide
              ? {
                  position: 'relative',
                  marginLeft: 0,
                  '& .edit-icon': {
                    position: 'absolute',
                    top: '0.4rem',
                    right: '0.3rem',
                    margin: 0,
                  },
                }
              : undefined
          }
        >
          {content}
        </EditablePropertyButton>
      ) : (
        <Box>{content}</Box>
      )}
    </>
  );
}
